import { HttpClient, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MSBody, MSHttpEvent, MSQuery, MSUrl, angularHttpEventToMSHttpEvent } from './utils/http.utils';

const BaseOptions = {
  observe: 'events' as const,
  reportProgress: true
};

@Injectable({
  providedIn: 'root'
})
export class HttpAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * The function sends an HTTP GET request and returns an Observable that emits:
   *  MSHttpEvent.Success containing the response data.
   *  MSHttpEvent.Loading when the request is sent.
   *
   * The Observable throws MSHttpEvent.Error if an error response is recieved.
   *
   * @param url - The `url` parameter is the URL of the HTTP request. It specifies the
   * location of the resource that you want to retrieve or interact with.
   * @param [query] - The `query` parameter is an optional object that represents the query
   * parameters to be included in the HTTP request.
   * @returns an Observable of type `MSHttpEvent.Spec`.
   */
  get(url: MSUrl, query?: MSQuery): Observable<MSHttpEvent.Spec> {
    const options = { params: query, ...BaseOptions };
    const responseObservable = this.httpClient.get(url, options);
    const msObservable = responseObservable.pipe(
      map((event) => this.mapResultToMSResult(event)),
      catchError((error) => this.mapErrorToMSError(error))
    );

    return msObservable;
  }

  /**
   * The function sends an HTTP POST request and returns an Observable that emits:
   *  MSHttpEvent.Success containing the response data.
   *  MSHttpEvent.Loading when the request is sent.
   *
   * The Observable throws MSHttpEvent.Error if an error response is recieved.
   *
   * @param url - The `url` parameter is the URL to which the HTTP POST request will be sent.
   * It specifies the destination of the request.
   * @param body - The `body` parameter is the data that you want to send in the HTTP
   * request body. It can be of any type, depending on the API you are calling.
   * @returns an Observable of type `MSHttpEvent.Spec`.
   */
  post(url: MSUrl, body: MSBody): Observable<MSHttpEvent.Spec> {
    const responseObservable = this.httpClient.post(url, body, BaseOptions);
    const msObservable = responseObservable.pipe(
      map((event) => this.mapResultToMSResult(event)),
      catchError((error) => this.mapErrorToMSError(error))
    );

    return msObservable;
  }

  private mapResultToMSResult(result: HttpEvent<Object>): MSHttpEvent.Spec {
    const msResult = angularHttpEventToMSHttpEvent(result);
    if (msResult instanceof MSHttpEvent.Success) {
      const successEvent = new MSHttpEvent.Success(msResult.status, msResult.data);
      return successEvent;
    }
    return msResult;
  }

  private mapErrorToMSError(error: HttpErrorResponse): Observable<MSHttpEvent.Error> {
    const errorEvent = new MSHttpEvent.Error(error.status, error.statusText);
    return throwError(errorEvent);
  }
}
